import { forwardRef, useId } from 'react';
import { cn, focusRing } from '../utils/cn';
import { DateRangePicker, type DateRange } from './DateRangePicker';
import { Icon } from './Icon';

export type PeriodPreset =
  'mtd' | 'qtd' | 'ytd' | 'last30' | 'last90' | 'lastQuarter' | 'lastYear' | 'custom';

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  mtd: 'Month to date',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  lastQuarter: 'Last quarter',
  lastYear: 'Last year',
  custom: 'Custom range',
};

export interface PeriodValue {
  preset: PeriodPreset;
  range: DateRange;
}

export interface PeriodSelectorProps {
  label?: string;
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  presets?: PeriodPreset[];
  disabled?: boolean;
  /** Suppressed in historical (as-of) views, where the period is fixed. */
  readOnlyReason?: string | null;
  className?: string;
}

export const PeriodSelector = forwardRef<HTMLSelectElement, PeriodSelectorProps>(
  function PeriodSelector(
    {
      label = 'Period',
      value,
      onChange,
      presets = ['mtd', 'qtd', 'ytd', 'last30', 'last90', 'lastQuarter', 'lastYear', 'custom'],
      disabled,
      readOnlyReason,
      className,
    },
    ref,
  ) {
    const baseId = useId();
    const isDisabled = disabled || Boolean(readOnlyReason);

    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label
          htmlFor={`${baseId}-preset`}
          className="text-dense-lg font-medium text-content-secondary"
        >
          {label}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex items-center">
            <select
              ref={ref}
              id={`${baseId}-preset`}
              value={value.preset}
              disabled={isDisabled}
              title={readOnlyReason ?? undefined}
              onChange={(event) =>
                onChange({ ...value, preset: event.target.value as PeriodPreset })
              }
              className={cn(
                'h-9 cursor-pointer appearance-none rounded-md border border-line bg-surface pl-3 pr-8 text-body text-content',
                'hover:border-line-strong disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-disabled',
                focusRing,
              )}
            >
              {presets.map((preset) => (
                <option key={preset} value={preset}>
                  {PERIOD_PRESET_LABELS[preset]}
                </option>
              ))}
            </select>
            <Icon
              name="chevron-down"
              size={15}
              className="pointer-events-none absolute right-2.5 text-content-muted"
            />
          </div>
          {value.preset === 'custom' ? (
            <DateRangePicker
              label="Custom period"
              hideLabel
              value={value.range}
              disabled={isDisabled}
              onChange={(range) => onChange({ ...value, range })}
            />
          ) : null}
        </div>
        {readOnlyReason ? (
          <p className="flex items-center gap-1 text-dense text-content-muted">
            <Icon name="lock" size={13} />
            {readOnlyReason}
          </p>
        ) : null}
      </div>
    );
  },
);
